import React, { useMemo } from 'react'
import { ColDef, IsRowSelectable } from 'ag-grid-community'
import { HStack } from '@musinsa/mwms-ds'
import GridTable from '@/components/table/GridTable'
import { GridTableBtnOption } from '@/components/table/grid_types'
import { OrderItem } from '@/services/outbound/outboundWaitingStatus/types'
import { ExcelDownloadType } from '@/services/excel/excel.types'
import { GRID_SIZE } from '@/utils/grid/grid'
import { ORDER_TYPES } from '@/services/outbound/outboundWaitingStatus/consts'
import { useUnallocatedReplanAction } from '@/services/outbound/outboundUnallocated/useUnallocatedReplanAction'
import { OPERATOR_REPLAN_BUTTON_TEXT } from '@/services/outbound/outboundUnallocated/replan'

interface OutboundUnallocatedTableProps {
  items?: OrderItem[]
  pageInfo?: PageInfoType
  isLoading?: boolean
  currentPage?: number
  onChangePage?: (page: number) => void
  onExportData?: (params?: ExcelDownloadType) => void
  /** 출고지시 접수 후 목록을 다시 읽는다. */
  onReplanDone?: () => void
}

const isOptimizerTargetRow: IsRowSelectable<OrderItem> = ({ data }) => data?.optimizerTarget === true

function OutboundUnallocatedTable({
  items,
  pageInfo,
  isLoading,
  currentPage,
  onChangePage,
  onExportData,
  onReplanDone,
}: OutboundUnallocatedTableProps) {
  const { enabled: isReplanEnabled, isPending: isReplanPending, requestReplan } = useUnallocatedReplanAction({ onDone: onReplanDone })

  // flag OFF 면 슬롯을 통째로 비운다 — `buttons` 가 비어 있어야 하단 액션 바 자체가 안 그려진다.
  // 선택 0건 비활성은 `GridTable` 이 `checkedNum` 으로 이미 처리하므로 여기서 또 막지 않는다.
  const buttons = useMemo<GridTableBtnOption<OrderItem>[] | undefined>(() => {
    if (!isReplanEnabled) return undefined

    return [
      {
        content: OPERATOR_REPLAN_BUTTON_TEXT,
        kind: 'secondary',
        disabled: isReplanPending,
        onClick: (selected, onDeselect) => {
          requestReplan(selected ?? [], onDeselect)
        },
      },
    ]
  }, [isReplanEnabled, isReplanPending, requestReplan])

  const columnDefs = useMemo<ColDef<OrderItem>[]>(() => {
    return [
      { field: 'orderStatus', headerName: '주문상태', width: 120 },
      { field: 'orderDateTime', headerName: '주문일', cellDataType: 'dateNday', pinned: 'left' },
      { field: 'collectedDate', headerName: '주문수집일', cellDataType: 'dateNday' },
      { field: 'logisticsServiceType', headerName: '화주유형', width: 94 },
      { field: 'shipperName', headerName: '화주명', width: 94 },
      { field: 'orderType', headerName: '주문유형', width: 94 },
      { field: 'transportType', headerName: '배송유형', width: 120 },
      { field: 'orderNumber', headerName: '주문번호', pinned: 'left' },
      { field: 'orderOptionNumber', headerName: '주문일련번호', pinned: 'left' },
      { field: 'refNo1', headerName: '화주사주문번호', width: 140 },
      { field: 'refNo2', headerName: '화주사주문일련번호', width: 140 },
      { field: 'etc1', headerName: '기타1', width: 140 },
      { field: 'etc2', headerName: '기타2', width: 140 },
      { field: 'goodsCode', headerName: '상품코드', width: 130 },
      { field: 'orderQuantity', headerName: '주문수량', width: 100 },
      { field: 'unallocatedQuantity', headerName: '미할당수량', width: 100 },
      {
        field: 'deliveryCompletePlanDate',
        headerName: '도착보장일',
        cellDataType: 'dateNday',
        valueGetter: (params) => (params.data?.orderType === ORDER_TYPES.PLUS ? params.data.deliveryCompletePlanDate : null),
      },
      { field: 'orderUserName', headerName: '주문자', width: 100, cellDataType: 'maskName' },
      { field: 'receiverName', headerName: '수령자', width: 100, cellDataType: 'maskName' },
      { field: 'receiverMobile', headerName: '핸드폰', width: 120, cellDataType: 'maskPhone' },
      { field: 'receiverPhone', headerName: '전화번호', width: 120, cellDataType: 'maskPhone' },
      { field: 'receiverZipcode', headerName: '우편번호', width: 90, cellDataType: 'maskPostalCode' },
      { field: 'receiverAddress1', headerName: '주소(기본)', width: 300, cellDataType: 'maskAddress' },
      { field: 'receiverAddress2', headerName: '주소(상세)', width: 300, cellDataType: 'maskAddress' },
      { field: 'deliveryPass', headerName: '출입방법', width: 300 },
      { field: 'deliveryMessage', headerName: '배송메시지' },
      { field: 'uid', headerName: 'UID', width: 130 },
      { field: 'brandName', headerName: '브랜드', width: 150 },
      { field: 'styleNo', headerName: '스타일넘버' },
      { field: 'barcode', headerName: '바코드' },
      { field: 'skuId', headerName: 'SKUID', width: 150 },
      { field: 'optionCode', headerName: '옵션코드', width: 120 },
      { field: 'goodsOption', headerName: '옵션', width: 100 },
      { field: 'goodsName', headerName: '상품명' },
      { field: 'gift', headerName: '사은품' },
    ]
  }, [])


  return (
    <HStack full gap={16}>
      <GridTable<OrderItem>
        isColumnNumPinned
        useColumnNo
        rowSelectable
        isRowSelectable={isOptimizerTargetRow}
        buttons={buttons}
        radius="0 0 8px 8px"
        isLoading={isLoading}
        rowData={items || []}
        exportType="private"
        exportServerAudit
        exportPersonalInfoOption
        onExportData={onExportData}
        onChangePage={onChangePage}
        countPerPage={GRID_SIZE.MEDIUM}
        currentPage={currentPage || 0}
        totalPages={pageInfo?.totalPages}
        totalCount={pageInfo?.totalCount || 0}
        columnDefs={columnDefs}
        gridKey="GI26_HEAD"
        legacyStorageKey="outbound-unallocated-table"
      />
    </HStack>
  )
}

export default OutboundUnallocatedTable
